import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { showDistrict } from '../../api/destinationApi';
import { Seo, Loading, EmptyContent } from '../shared';
import MediaFrame from '../shared/MediaFrame';
import DestinationCard from './DestinationCard';
import '../../styles/destinations.scss';
import '../../styles/media.scss';

function DistrictDetailPage() {
	const { slug } = useParams();
	const [district, setDistrict] = useState(null);
	const [loading, setLoading] = useState(true);
	const [notFound, setNotFound] = useState(false);

	useEffect(() => {
		setLoading(true);
		setNotFound(false);
		showDistrict(slug)
			.then((res) => setDistrict(res.data))
			.catch(() => setNotFound(true))
			.finally(() => setLoading(false));
	}, [slug]);

	if (loading) return <Loading />;

	if (notFound || !district) {
		return (
			<div className="container py-5">
				<EmptyContent>
					<i className="fas fa-map text-muted" style={{ fontSize: '2rem' }} aria-hidden="true" />
					<h3 className="mt-3">District not found</h3>
					<Link to="/destinations" className="btn btn-primary mt-2">Browse all destinations</Link>
				</EmptyContent>
			</div>
		);
	}

	const d = district;
	const climate = (d.climate || []).filter((c) => c.avg_high_c != null);
	const destinations = d.destinations || [];
	const municipalities = d.municipalities || [];

	return (
		<div className="destination-detail">
			<Seo
				title={`${d.name} District`}
				path={`/districts/${d.slug}`}
				description={d.description || `Explore ${d.name} district, ${d.province ? d.province.name + ' Province' : ''} — destinations, climate, and practical travel information.`}
			/>

			<MediaFrame category="district" seed={d.slug} alt={`${d.name} District`} variant="hero" className="destination-hero">
				<div className="destination-hero__overlay" />
				<div className="container destination-hero__content">
					<nav className="destination-breadcrumb" aria-label="Breadcrumb">
						<Link to="/destinations">Destinations</Link>
						{d.province && <> / <span>{d.province.name} Province</span></>}
					</nav>
					{d.region && <span className="destination-hero__category">{d.region.name} Region</span>}
					<h1 className="text-white">{d.name} District</h1>
					{d.description && <p className="text-white destination-hero__summary">{d.description}</p>}
				</div>
			</MediaFrame>

			<div className="container destination-detail__body">
				<div className="quick-facts">
					{d.province && (
						<div className="quick-fact">
							<i className="fas fa-landmark" aria-hidden="true" />
							<div>
								<span className="quick-fact__label">Province</span>
								<span className="quick-fact__value">{d.province.name}</span>
							</div>
						</div>
					)}
					{d.region && (
						<div className="quick-fact">
							<i className="fas fa-mountain" aria-hidden="true" />
							<div>
								<span className="quick-fact__label">Terrain</span>
								<span className="quick-fact__value">{d.region.name}</span>
							</div>
						</div>
					)}
					<div className="quick-fact">
						<i className="fas fa-map-marker-alt" aria-hidden="true" />
						<div>
							<span className="quick-fact__label">Destinations</span>
							<span className="quick-fact__value">{destinations.length}</span>
						</div>
					</div>
				</div>

				{destinations.length > 0 && (
					<section className="destination-section">
						<h2>Destinations in {d.name}</h2>
						<div className="destinations-grid">
							{destinations.map((dest) => (
								<DestinationCard key={dest.slug} destination={dest} />
							))}
						</div>
					</section>
				)}

				{climate.length > 0 && (
					<section className="destination-section">
						<h2>Monthly Climate</h2>
						<div className="climate-table-wrap">
							<table className="climate-table">
								<thead>
									<tr>
										<th>Month</th>
										<th>High / Low</th>
										<th>Rainfall</th>
										<th>Notes</th>
									</tr>
								</thead>
								<tbody>
									{climate.map((c) => (
										<tr key={c.month}>
											<td>{c.month_name}</td>
											<td>{c.avg_high_c}°C / {c.avg_low_c}°C</td>
											<td>{c.rainfall_mm}mm</td>
											<td className="climate-table__notes">{c.notes}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</section>
				)}

				{municipalities.length > 0 && (
					<section className="destination-section">
						<h2>Municipalities</h2>
						<div className="row">
							{municipalities.map((m) => (
								<div key={m.slug} className="col-md-6 mb-3">
									<div className="practical-info" style={{ marginBottom: 0 }}>
										<h3>{m.name}{m.kind ? ` — ${m.kind}` : ''}</h3>
										{m.mobile_network_notes && (
											<div className="practical-info__row">
												<i className="fas fa-signal" aria-hidden="true" />
												<div>
													<span className="practical-info__label">Mobile network</span>
													<span className="practical-info__value">{m.mobile_network_notes}</span>
												</div>
											</div>
										)}
										{m.transport_notes && (
											<div className="practical-info__row">
												<i className="fas fa-bus" aria-hidden="true" />
												<div>
													<span className="practical-info__label">Transport</span>
													<span className="practical-info__value">{m.transport_notes}</span>
												</div>
											</div>
										)}
									</div>
								</div>
							))}
						</div>
					</section>
				)}
			</div>
		</div>
	);
}

export default DistrictDetailPage;
